import { PROTECTION_TIERS } from "./protectionTiers";

// autoCatalogue.js — the Automotive calculator's services, protection
// systems, add-ons, detailing and pricing factors. Shared by the calculator
// (pricing) and the client quote template (labels), so the quote can never
// use different names than the calculator.

export const AUTO_MACHINE_PRESETS={
  "Champ Vario":    {dryIceKgHr:15, fuelBurnLHr:9.5},
  "Champ Ultra":    {dryIceKgHr:20, fuelBurnLHr:11.0},
  "Karcher IB 15/120":{dryIceKgHr:12, fuelBurnLHr:8.5},
  "Cold Jet Aero 40": {dryIceKgHr:18, fuelBurnLHr:10.5},
  "Custom":         {dryIceKgHr:15, fuelBurnLHr:10.0},
};

export const AUTO_SERVICE_HOURS={
  // 9.25h from 4 Oct 2026 (was 5.5h; 4.0h before 3 Oct). Breakdown, to tune later:
  //   setup, masking, photos                     0.5h
  //   undertrays off and refit                   0.75h
  //   wheels off, protect, rack, refit, torque   1.5h
  //   underbody blast                            5.0h
  //   wheel backs, barrels, calipers             1.0h
  //   de-mask, inspection, clean-down            0.5h
  // Contamination and vehicle-type multipliers apply on top. Saved quotes keep
  // the total they were saved with; only new or amended quotes use this.
  // blastFrac: only the 6.0h of blasting (underbody + wheel backs) uses dry ice
  // and runs the machine; the other 3.25h is labour only.
  fullUnderbody:    {label:"Full Underbody Package",      hrs:9.25, diMult:1.0, blastFrac:6.0/9.25},
  underbodyOnly:    {label:"Floorpan & Sills",             hrs:2.5, diMult:0.8},
  wheelArches:      {label:"Wheel Arches (inc. liner removal where fitted)", hrs:1.5, diMult:0.6},
  suspensionBrakes: {label:"Suspension & Brakes",         hrs:1.5, diMult:0.7},
  engineBay:        {label:"Engine Bay",                   hrs:2.0, diMult:0.7},
  drivetrain:       {label:"Drivetrain / Gearbox",        hrs:1.5, diMult:0.6},
  exhaust:          {label:"Exhaust System",               hrs:1.0, diMult:0.4},
  fullExteriorDetail:{label:"Full Exterior Detail",       hrs:3.0, diMult:0.9},
  concoursInterior: {label:"Concours Interior Prep",      hrs:2.5, diMult:0.5},
  underseelRemoval: {label:"Underseal / Bitumen Removal", hrs:3.0, diMult:1.2},
};

// Services that already include others. While the containing service is
// ticked, the included ones are greyed out ("Included in …") so the same
// area can't be charged twice; they stay available on their own for
// partial jobs. Audit (Oct 2026): Full Underbody is the only service that
// fully contains others.
export const AUTO_SERVICE_INCLUDES={
  fullUnderbody:["underbodyOnly","wheelArches","suspensionBrakes","drivetrain","exhaust"],
};
// The ticked service (if any) that includes this one.
export function includedBy(key,services){
  return (services||[]).find(k=>(AUTO_SERVICE_INCLUDES[k]||[]).includes(key))||null;
}

export const AUTO_CONTAM_MULT={
  Light:    {label:"Light",    mult:1.0},
  Moderate: {label:"Moderate", mult:1.35},
  Heavy:    {label:"Heavy",    mult:1.75},
  Severe:   {label:"Severe",   mult:2.2},
};

// Campervan / Motorhome: a bigger vehicle, so its service hours are scaled up,
// and so is the protection tier's materials and labour (much larger
// underbody). Only this type has a protection scale - every other type stays
// at 1.0, so no saved quote changes price.
export const CAMPERVAN_SERVICE_MULT=1.5;
export const CAMPERVAN_PROTECTION_SCALE=1.8;
export const AUTO_PROTECTION_SCALE={"Campervan / Motorhome":CAMPERVAN_PROTECTION_SCALE};

export const AUTO_VEHICLE_MULT={
  "Campervan / Motorhome": CAMPERVAN_SERVICE_MULT,
  "Classic / Vintage":    1.3,
  "Performance / Supercar": 1.2,
  "4x4 / SUV":            1.25,
  "Modern / Daily":       1.0,
  "Race / Rally Car":     1.4,
  "Motorcycle":           0.7,
};

// Protection hours are labour only: like custom labour they don't run the
// blaster, so they add no dry ice, electricity/fuel or equipment cost.
// From 18 Oct 2026 (was lanoguard 0.5h £45, biltHamber 1.0h £65, dinitrol
// 1.5h £120, ceramic 2.0h £280). Saved quotes keep the total they were saved with.
// Dinitrol materials £100 -> £145 from 30 Oct 2026: Leading Solvents trade
// prices for Gareth's large 4x4 kit (6L ML, 8L 4941, RC900, plugs, nozzle)
// come to about £181 ex VAT, and a 4x4 scales £145 x 1.25 = £181.25.
export const AUTO_PROTECTION={
  none:       {label:"None",                            materials:0,   labourHrs:0},
  // Labels (and all client-facing protection wording) come from protectionTiers.js.
  lanoguard:  {label:PROTECTION_TIERS.lanoguard.label,  materials:45,  labourHrs:1.0},
  biltHamber: {label:PROTECTION_TIERS.biltHamber.label, materials:50,  labourHrs:2.0},
  dinitrol:   {label:PROTECTION_TIERS.dinitrol.label,   materials:145, labourHrs:3.5},
  ceramic:    {label:PROTECTION_TIERS.ceramic.label,    materials:280, labourHrs:4.0},
};

export const AUTO_ADDONS={
  rustStabilisation: {label:"Rust Stabilisation Treatment", materials:45,  labourHrs:1.0},
  headlightRestore:  {label:"Headlight Restoration",        materials:25,  labourHrs:1.0},
  cavityWax:         {label:"Cavity Wax Injection",         materials:55,  labourHrs:0.75},
  wheelRemoval:      {label:"Wheel Removal & Refitting",    materials:0,   labourHrs:1.5},
  undertraRemoval:   {label:"Undertray Removal & Refit",    materials:0,   labourHrs:0.75},
};

// Add-ons a service already covers. While the service is ticked they show
// ticked, greyed out and "Included", add no hours and stay off the client
// quote's Additional Services row (the service's scope mentions them). Full
// Underbody's 9.25h already includes wheels off and undertrays out (see its breakdown above).
export const AUTO_ADDON_INCLUDES={
  fullUnderbody:["wheelRemoval","undertraRemoval"],
};
// Add-ons a protection system already covers, the same way: Dinitrol Full
// System includes its own cavity wax injection.
export const AUTO_PROTECTION_ADDON_INCLUDES={
  dinitrol:["cavityWax"],
};
// The ticked service (if any) that includes this add-on.
export function addonIncludedBy(key,services){
  return (services||[]).find(k=>(AUTO_ADDON_INCLUDES[k]||[]).includes(key))||null;
}
// Name of whatever covers this add-on on the quote - a ticked service or the
// selected protection system - or null.
export function addonIncludedIn(key,services,protection){
  const svc=addonIncludedBy(key,services);
  if(svc) return AUTO_SERVICE_HOURS[svc].label;
  return (AUTO_PROTECTION_ADDON_INCLUDES[protection]||[]).includes(key)?AUTO_PROTECTION[protection].label:null;
}
// Internal add-ons line (job sheet, summary): charged add-ons, then the ones a
// ticked service or the protection system covers, so the crew still knows to do them.
export function autoAddonsSummary(addons,services,protection){
  const charged=(addons||[]).map(k=>AUTO_ADDONS[k]?.label).filter(Boolean);
  const included=Object.keys(AUTO_ADDONS).filter(k=>!(addons||[]).includes(k)&&addonIncludedIn(k,services,protection))
    .map(k=>`${AUTO_ADDONS[k].label} (included in ${addonIncludedIn(k,services,protection)})`);
  return [...charged,...included].join(", ");
}

export const AUTO_DETAILING={
  exteriorDetail:  {label:"Full Exterior Detail",        desc:"Machine polish, decontamination & ceramic/wax protection", subcontractCost:350, clientPrice:450},
  interiorDetail:  {label:"Full Interior Detail",        desc:"Deep clean, leather treatment, steam & carpet extraction",  subcontractCost:250, clientPrice:320},
  fullDetail:      {label:"Full Detail (Interior & Exterior)", desc:"Complete interior and exterior detail package",        subcontractCost:550, clientPrice:695},
  paintCorrection: {label:"Paint Correction (single stage)", desc:"Machine polish to remove swirls and light defects",     subcontractCost:400, clientPrice:525},
  paintCorrectionMulti: {label:"Paint Correction (multi stage)", desc:"Two-stage correction for heavily defected paintwork", subcontractCost:650, clientPrice:850},
  ceramicCoating:  {label:"Ceramic Coating (3yr exterior)", desc:"Professional grade 3 year ceramic coating application",  subcontractCost:600, clientPrice:795},
  ceramicCoating5yr:{label:"Ceramic Coating (5yr exterior)", desc:"Premium 5 year ceramic coating, prep and application",   subcontractCost:900, clientPrice:1195},
};

// Longevity per protection system (from protectionTiers.js).
export const AUTO_PROTECTION_LONGEVITY=Object.fromEntries(Object.entries(PROTECTION_TIERS).map(([k,v])=>[k,v.longevity]));

// ─── Job stages (client quote order) ─────────────────────────────────────────
// The Automotive job in the order the work is done: Clean → Rust treatment →
// Protection → Additional work → Add-ons. The ONE source for the client
// quote's At a glance, Technical Summary and Scope of Works, so all three
// list the same stages, in the same order, under the same names.
export const AUTO_STAGE_NAMES={clean:"Clean",rust:"Rust treatment",protection:"Protection",additional:"Additional work",addOns:"Add-ons"};
// state: a quote's (saved or live) Automotive state.
export function autoStages({services,addons,protection,customLabour,concierge,detailing}={}){
  const ad=addons||[];
  const rust=ad.includes("rustStabilisation");
  // Detailing is done by a partner but paid for by the client, so it's listed
  // by its client-facing name only - never the partner, cost or description.
  const det=(detailing||[]).filter(k=>AUTO_DETAILING[k]).map(k=>AUTO_DETAILING[k].label);
  return {
    clean:(services||[]).filter(k=>AUTO_SERVICE_HOURS[k]),
    // Rust Stabilisation, with the product named when it's the Dinitrol system's RC900.
    rust:rust?{label:AUTO_ADDONS.rustStabilisation.label,product:protection==="dinitrol"?"Dinitrol RC900":""}:null,
    protection:protection&&protection!=="none"&&AUTO_PROTECTION[protection]?AUTO_PROTECTION[protection].label:"",
    // Custom labour the client sees: rows not marked internal (rows saved
    // before the Internal only box existed have no flag, so they show).
    additional:(customLabour||[]).filter(r=>!r?.internal).map(r=>String(r?.label||"").trim()).filter(Boolean),
    detailing:det,
    addOns:[...ad.filter(k=>k!=="rustStabilisation"&&AUTO_ADDONS[k]).map(k=>AUTO_ADDONS[k].label),...det,...(concierge?["Collection and delivery"]:[])],
  };
}
